/**
 * Die Seiten des normalen IBN-Ablaufs, auf denen der Assistent erscheint.
 */
export type IbnWizardSeite =
  | 'projekt'
  | 'import'
  | 'liste'
  | 'messstelle';


/**
 * Weg nach der Auswahl im Projekt:
 * - neu:       Excel-Import → Messstellen → Prüfpunkte
 * - bestehend: vorhandene Inbetriebnahme → Messstellen → Prüfpunkte
 */
export type IbnWizardPfad =
  | 'neu'
  | 'bestehend';


export interface IbnWizardSchritt {

  id: string;

  /** Seite, auf der der Schritt angezeigt wird. */
  seite: IbnWizardSeite;

  titel: string;

  text: string;

  /**
   * Werte von `data-wizard-target`, auf die der Schritt zeigt.
   * Das erste sichtbare Element gewinnt; ohne Ziel erscheint das Popup mittig.
   */
  ziele?: string[];

  /** Fehlt das Ziel (z. B. ohne automatische Erkennung), wird der Schritt übersprungen. */
  optional?: boolean;

  /** `data-wizard-target`, das existieren muss, bevor „Weiter“ möglich ist. */
  bereitWenn?: string;

  /** Hinweis, solange „Weiter“ gesperrt ist. */
  wartetText?: string;

  /** „Weiter“ klickt das Ziel an – der Seitenwechsel schaltet dann weiter. */
  weiterKlicktZiel?: boolean;

  /** „Weiter“ bleibt gesperrt; der Schritt endet nur durch einen Seitenwechsel. */
  wartetAufSeitenwechsel?: boolean;

  /** Kein „Weiter“-Button – die Auswahl erfolgt auf der Seite selbst. */
  ohneWeiter?: boolean;

  /**
   * Hier wählt der Benutzer den Weg. Welcher es wird, ergibt sich aus der
   * folgenden Navigation (Importseite = neu, Messstellenliste = bestehend).
   */
  verzweigung?: boolean;

  /** Gehört nur zum Weg „neue Inbetriebnahme“ (Excel-Import). */
  nurNeu?: boolean;

  /** Nach einem Import führt von hier kein Weg zurück (gilt nur im Weg „neu“). */
  keinZurueck?: boolean;
}


export const IBN_WIZARD_SCHRITTE: IbnWizardSchritt[] = [

  // ── Projekt ──

  {
    id: 'einfuehrung',
    seite: 'projekt',
    titel: 'Willkommen beim IBN-Assistenten',
    text:
      'Der Assistent zeigt dir Schritt für Schritt den Ablauf einer Inbetriebnahme: '
      + 'Messstellenliste importieren, Messstellen öffnen und Prüfpunkte abarbeiten.'
  },
  {
    id: 'ibn-auswaehlen',
    seite: 'projekt',
    ziele: ['projekt-inbetriebnahmen'],
    verzweigung: true,
    ohneWeiter: true,
    wartetAufSeitenwechsel: true,
    wartetText: 'Klicke auf „Neue Liste hinzufügen“ oder auf eine vorhandene Inbetriebnahme.',
    titel: 'Inbetriebnahme auswählen',
    text:
      'Du kannst eine neue Inbetriebnahme aus einer Excel-Messstellenliste erstellen '
      + 'oder eine bestehende IBN öffnen und dort direkt mit der Durchführung fortfahren.'
  },

  // ── Import (nur neue Inbetriebnahme) ──

  {
    id: 'datei',
    seite: 'import',
    nurNeu: true,
    ziele: ['import-datei'],
    bereitWenn: 'import-abschluss',
    wartetText: 'Weiter ist möglich, sobald die Datei analysiert wurde.',
    titel: 'Excel-Datei auswählen',
    text:
      'Wähle hier die Messstellenliste für die Inbetriebnahme aus. '
      + 'Die Datei wird zunächst nur gelesen und analysiert – gespeichert wird noch nichts.'
  },
  {
    id: 'erkennung',
    seite: 'import',
    nurNeu: true,
    ziele: ['import-erkennung'],
    optional: true,
    titel: 'Erkannte Struktur prüfen',
    text:
      'Hier stehen Arbeitsblatt, Kopfbereich und Kennzeichnung, mit denen die Liste gelesen wird. '
      + 'Passt etwas nicht, korrigierst du es über „Erkennung bearbeiten“.'
  },
  {
    id: 'anlagenteile',
    seite: 'import',
    nurNeu: true,
    ziele: ['import-anlagenteile'],
    optional: true,
    titel: 'Anlagenteile prüfen',
    text:
      'Die Messstellen werden Anlagenteilen zugeordnet. Kontrolliere die erkannte Spalte – '
      + 'über „Zuordnungen prüfen“ passt du Klartexte und Regeln an.'
  },
  {
    id: 'kategorien',
    seite: 'import',
    nurNeu: true,
    ziele: ['import-kategorien'],
    optional: true,
    titel: 'Kategorien prüfen',
    text:
      'Die Kategorie bestimmt später, welche Prüfpunkte eine Messstelle bekommt. '
      + 'Über „Zuordnungen prüfen“ kontrollierst du die vorgeschlagenen Regeln.'
  },
  {
    id: 'spalten',
    seite: 'import',
    nurNeu: true,
    ziele: ['import-spalten', 'import-vorschau'],
    optional: true,
    titel: 'Spalten anpassen',
    text:
      'Die Vorschau zeigt, wie die Liste nach dem Import aussieht. '
      + 'Über „Spalten anpassen“ legst du fest, welche Excel-Spalten übernommen werden.'
  },
  {
    id: 'zeilen',
    seite: 'import',
    nurNeu: true,
    ziele: ['import-zeilen', 'import-vorschau'],
    optional: true,
    titel: 'Zeilenauswahl prüfen',
    text:
      'Nicht jede Excel-Zeile ist eine Messstelle. Nach der automatischen Zeilenprüfung '
      + 'kontrollierst du über „Zeilenauswahl bearbeiten“, welche Zeilen importiert werden.'
  },
  {
    id: 'importieren',
    seite: 'import',
    nurNeu: true,
    ziele: ['import-abschluss'],
    wartetAufSeitenwechsel: true,
    wartetText: 'Nach dem erfolgreichen Import geht es automatisch weiter.',
    titel: 'Jetzt importieren',
    text:
      'Prüfe noch den Namen der Liste. Erst „Jetzt importieren“ legt die Inbetriebnahme an.'
  },

  // ── Messstellenliste ──

  {
    id: 'liste',
    seite: 'liste',
    ziele: ['liste-filter'],
    keinZurueck: true,
    titel: 'Messstellen finden',
    text:
      'Hier findest du alle Messstellen der Inbetriebnahme. Suche und filtere sie '
      + 'nach Kennzeichen, Kategorie, Status oder Anlagenteil.'
  },
  {
    id: 'messstelle-oeffnen',
    seite: 'liste',
    ziele: ['liste-messstelle'],
    weiterKlicktZiel: true,
    titel: 'Messstelle öffnen',
    text:
      'Ein Klick auf eine Messstelle öffnet ihre Detailseite. '
      + 'Wähle eine Messstelle – oder klicke auf „Weiter“, dann wird die erste geöffnet.'
  },

  // ── Messstelle ──

  {
    id: 'kategorie',
    seite: 'messstelle',
    ziele: ['detail-kategorie'],
    titel: 'Kategorie prüfen',
    text:
      'Kontrolliere zuerst die Kategorie. Sie legt fest, welche Prüfpunkte die Messstelle bekommt.'
  },
  {
    id: 'pruefpunkte',
    seite: 'messstelle',
    ziele: ['detail-pruefpunkte'],
    titel: 'Prüfpunkte abarbeiten',
    text:
      'Setze für jeden Prüfpunkt das Ergebnis und ergänze bei Bedarf eine Bemerkung. '
      + 'Bei „nicht bestanden“ ist eine Fehlerbeschreibung Pflicht.'
  },
  {
    id: 'abschluss',
    seite: 'messstelle',
    titel: 'Geschafft!',
    text:
      'Das ist der grundlegende Ablauf einer Inbetriebnahme: Inbetriebnahme anlegen oder öffnen, '
      + 'Messstellen finden, Kategorie prüfen und Prüfpunkte abarbeiten. Über den Zurück-Pfeil '
      + 'geht es zur Liste und zur nächsten Messstelle.'
  }
];
